import type { QuizDetail } from '@cfa/pathway-fe-shared/xp';

import type { QuizEditorQuestions } from './quizEditorTypes';

export interface ServerQuestionIdentifiers {
  referenceIds: ReadonlySet<string>;
  ids: ReadonlySet<string>;
  answerReferenceIds: ReadonlySet<string>;
  answerIds: ReadonlySet<string>;
}

type QuizQuestionsSource = QuizEditorQuestions | QuizDetail['questions'];
type QuizQuestionSource = QuizQuestionsSource[number];
type QuizAnswerSource = QuizQuestionSource['answers'][number];

export const emptyServerQuestionIdentifiers =
  (): ServerQuestionIdentifiers => ({
    referenceIds: new Set(),
    ids: new Set(),
    answerReferenceIds: new Set(),
    answerIds: new Set(),
  });

export const hasServerSnapshot = (
  serverIds: ServerQuestionIdentifiers,
): boolean =>
  serverIds.referenceIds.size > 0 ||
  serverIds.ids.size > 0 ||
  serverIds.answerReferenceIds.size > 0 ||
  serverIds.answerIds.size > 0;

const collectLocalizedIdValues = (
  record: Record<string, string> | null | undefined,
): string[] => {
  if (!record) {
    return [];
  }

  return Object.values(record).filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
};

export const collectServerQuestionIdentifiers = (
  questions: QuizQuestionsSource,
): ServerQuestionIdentifiers => {
  const editorQuestions = questions as QuizEditorQuestions;
  const referenceIds = new Set<string>();
  const ids = new Set<string>();
  const answerReferenceIds = new Set<string>();
  const answerIds = new Set<string>();

  for (const question of editorQuestions) {
    for (const value of collectLocalizedIdValues(question.referenceId)) {
      referenceIds.add(value);
    }
    for (const value of collectLocalizedIdValues(question.id)) {
      ids.add(value);
    }

    for (const answer of question.answers) {
      for (const value of collectLocalizedIdValues(answer.referenceId)) {
        answerReferenceIds.add(value);
      }
      for (const value of collectLocalizedIdValues(answer.id)) {
        answerIds.add(value);
      }
    }
  }

  return { referenceIds, ids, answerReferenceIds, answerIds };
};

export const resolveServerQuestionIdentifiers = (
  cached: ServerQuestionIdentifiers,
  sourceQuestions: QuizQuestionsSource | undefined,
): ServerQuestionIdentifiers => {
  if (hasServerSnapshot(cached)) {
    return cached;
  }

  if (sourceQuestions) {
    return collectServerQuestionIdentifiers(sourceQuestions);
  }

  return cached;
};

const questionIsPersisted = (
  question: QuizQuestionSource,
  serverIds: ServerQuestionIdentifiers,
): boolean =>
  collectLocalizedIdValues(question.referenceId).some(value =>
    serverIds.referenceIds.has(value),
  ) ||
  collectLocalizedIdValues(question.id).some(value => serverIds.ids.has(value));

const answerIsPersisted = (
  answer: QuizAnswerSource,
  serverIds: ServerQuestionIdentifiers,
): boolean =>
  collectLocalizedIdValues(answer.referenceId).some(value =>
    serverIds.answerReferenceIds.has(value),
  ) ||
  collectLocalizedIdValues(answer.id).some(value =>
    serverIds.answerIds.has(value),
  );

const sanitizeAnswerForSave = (
  answer: QuizAnswerSource,
  serverIds: ServerQuestionIdentifiers,
  questionIsNew: boolean,
): QuizAnswerSource => {
  if (!questionIsNew && answerIsPersisted(answer, serverIds)) {
    const keepReferenceId = collectLocalizedIdValues(answer.referenceId).some(
      value => serverIds.answerReferenceIds.has(value),
    )
      ? answer.referenceId
      : undefined;
    const keepId = collectLocalizedIdValues(answer.id).some(value =>
      serverIds.answerIds.has(value),
    )
      ? answer.id
      : undefined;

    const { referenceId: _referenceId, id: _id, ...rest } = answer;
    return {
      ...rest,
      ...(keepReferenceId ? { referenceId: keepReferenceId } : {}),
      ...(keepId ? { id: keepId } : {}),
    };
  }

  const { referenceId: _referenceId, id: _id, ...rest } = answer;
  return rest;
};

const stripReferenceIdForNewQuestions = (
  questions: QuizEditorQuestions,
  serverIds: ServerQuestionIdentifiers,
): QuizEditorQuestions =>
  questions.map(question => {
    const questionIsNew = !questionIsPersisted(question, serverIds);
    const sanitizedAnswers = question.answers.map(answer =>
      sanitizeAnswerForSave(answer, serverIds, questionIsNew),
    );

    if (questionIsNew) {
      const { referenceId: _referenceId, id: _id, ...rest } = question;
      return { ...rest, answers: sanitizedAnswers };
    }

    const keepReferenceId = collectLocalizedIdValues(question.referenceId).some(
      value => serverIds.referenceIds.has(value),
    )
      ? question.referenceId
      : undefined;
    const keepId = collectLocalizedIdValues(question.id).some(value =>
      serverIds.ids.has(value),
    )
      ? question.id
      : undefined;

    const {
      referenceId: _referenceId,
      id: _id,
      ...rest
    } = { ...question, answers: sanitizedAnswers };

    return {
      ...rest,
      answers: sanitizedAnswers,
      ...(keepReferenceId ? { referenceId: keepReferenceId } : {}),
      ...(keepId ? { id: keepId } : {}),
    };
  }) as QuizEditorQuestions;

const parseQuizQuestionsForSave = (
  questions: QuizQuestionsSource,
): QuizEditorQuestions => {
  const editorQuestions = questions as QuizEditorQuestions;
  return editorQuestions.map(question => ({
    ...question,
    question: {
      ...question.question,
      en:
        question.question.en === '' && question.question.es
          ? question.question.es
          : (question.question.en ?? ''),
    },
    answers: question.answers.map(answer => ({
      ...answer,
      answer: {
        ...answer.answer,
        en:
          answer.answer.en === '' && answer.answer.es
            ? answer.answer.es
            : (answer.answer.en ?? ''),
      },
    })),
  }));
};

export const prepareQuestionsForSave = (
  questions: QuizQuestionsSource,
  serverIds: ServerQuestionIdentifiers,
  options: { isCreate?: boolean } = {},
) => {
  const parsed = parseQuizQuestionsForSave(questions);

  if (!options.isCreate && !hasServerSnapshot(serverIds)) {
    return parsed;
  }

  return stripReferenceIdForNewQuestions(parsed, serverIds);
};
